import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import './index.css';

const SERVER_URL = 'https://eu.apk-events.xyz/api';
// const SERVER_URL = 'http://localhost:8087';
const FEED_PAGE_SIZE = 15;
const FEED_PRIORITY_MIN = 20;
const FEED_PRIORITY_MAX = 100;
const FEED_SORT_FIELDS = {
	PRIORITY: 'priority',
	DATE: 'date',
};
const FEED_SORT_DIRECTIONS = {
	ASC: 'asc',
	DESC: 'desc',
};
const FEED_SORT_DIRECTION_OPTIONS = [
	{ id: FEED_SORT_DIRECTIONS.ASC, label: 'ArrowUp' },
	{ id: FEED_SORT_DIRECTIONS.DESC, label: 'ArrowDown' },
];
const FEED_SORT_OPTIONS = [
	{ id: FEED_SORT_FIELDS.PRIORITY, label: 'Priority' },
	{ id: FEED_SORT_FIELDS.DATE, label: 'Date' },
];

const QUERY_TAB_ID = 'query';
const FEED_TAB_ID = 'feed';
const POLYMARKET_TAB_ID = 'polymarket';
const WEB_NEWS_TAB_ID = 'webnews';
const DAILY_PERIOD_ID = 'daily';
const WEEKLY_PERIOD_ID = 'weekly';
const MONTHLY_PERIOD_ID = 'monthly';
const FEED_SOURCE_ALL = 'ALL';

const NEWS_PERIODS = [
	{ id: DAILY_PERIOD_ID, label: 'Daily', path: '/news/day' },
	{ id: WEEKLY_PERIOD_ID, label: 'Weekly', path: '/news/week' },
	{ id: MONTHLY_PERIOD_ID, label: 'Monthly', path: '/news/month' },
];

const FEED_SOURCE_TYPES = [
	{ value: FEED_SOURCE_ALL, label: 'All' },
	{ value: 'INVESTINGCOM', label: 'Investing.com' },
	{ value: 'TELEGRAMNEWS', label: 'Telegram News' },
	{ value: 'WEB', label: 'Web' },
];

const SOURCE_TYPES = [
	{ value: 'TELEGRAMNEWS', label: 'Telegram News' },
	{ value: 'INVESTINGCOM', label: 'Investing.com' },
];

const NEWS_TABS = SOURCE_TYPES.map((source) => ({
	id: source.value,
	label: source.label,
	type: 'news',
	sourceType: source.value,
}));

const WEB_NEWS_TAB = {
	id: WEB_NEWS_TAB_ID,
	label: 'Web News',
	path: '/news/day',
	sourceType: 'WEB',
};

const TABS = [
	...NEWS_TABS,
	WEB_NEWS_TAB,
	{ id: POLYMARKET_TAB_ID, label: 'Polymarket', path: '/polymarket/overview' },
	{ id: FEED_TAB_ID, label: 'Feed' },
	{ id: QUERY_TAB_ID, label: 'Topics' },
];

const normalizeDate = (value) => {
	const date = new Date(value);
	date.setHours(0, 0, 0, 0);
	return date;
};

const formatDateForApi = (date) => {
	if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
		return '';
	}

	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');

	return `${year}-${month}-${day}`;
};

const getPriorityValue = (entry) => {
	if (typeof entry?.priority === 'number') {
		return entry.priority;
	}

	if (typeof entry?.priotity === 'number') {
		return entry.priotity;
	}

	return null;
};

const getFeedItemDateValue = (entry) => {
	if (typeof entry?.timestamp === 'number') {
		return entry.timestamp * 1000;
	}

	if (entry?.date) {
		const parsed = Date.parse(entry.date);
		if (!Number.isNaN(parsed)) {
			return parsed;
		}
	}

	return null;
};

const parseUtcDateString = (value) => {
	if (!value) {
		return null;
	}

	const trimmed = value.trim();
	const hasExplicitTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed);

	if (hasExplicitTimezone) {
		const parsed = Date.parse(trimmed);
		return Number.isNaN(parsed) ? null : new Date(parsed);
	}

	const normalized = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
	const candidate = normalized.endsWith('Z') ? normalized : `${normalized}Z`;
	const parsedCandidate = Date.parse(candidate);

	if (!Number.isNaN(parsedCandidate)) {
		return new Date(parsedCandidate);
	}

	const fallbackParsed = Date.parse(trimmed);
	return Number.isNaN(fallbackParsed) ? null : new Date(fallbackParsed);
};

export const NewsContainer = () => {
	const [activeTab, setActiveTab] = useState(TABS[0].id);
	const [selectedPeriods, setSelectedPeriods] = useState(() =>
		NEWS_TABS.reduce((acc, tab) => {
			acc[tab.id] = DAILY_PERIOD_ID;
			return acc;
		}, {}),
	);
	const [dailyDate, setDailyDate] = useState(() => normalizeDate(new Date()));
	const [loadingTab, setLoadingTab] = useState(null);
	const [tabResults, setTabResults] = useState({});
	const [tabErrors, setTabErrors] = useState({});
	const [queryValue, setQueryValue] = useState('');
	const [queryLoading, setQueryLoading] = useState(false);
	const [queryResult, setQueryResult] = useState(null);
	const [queryError, setQueryError] = useState(null);
	const [queryMinPriority, setQueryMinPriority] = useState(FEED_PRIORITY_MIN);
	const [querySortField, setQuerySortField] = useState(FEED_SORT_FIELDS.PRIORITY);
	const [querySortDirection, setQuerySortDirection] = useState(FEED_SORT_DIRECTIONS.DESC);
	const [expandedQueryIds, setExpandedQueryIds] = useState(() => new Set());
	const [queryHasFetched, setQueryHasFetched] = useState(false);
	const [trends, setTrends] = useState([]);
	const [trendsLoading, setTrendsLoading] = useState(false);
	const [trendsError, setTrendsError] = useState(null);
	const [feedStartDate, setFeedStartDate] = useState(() => normalizeDate(new Date()));
	const [feedEndDate, setFeedEndDate] = useState(() => {
		const initial = new Date();
		initial.setDate(initial.getDate() + 1);
		return normalizeDate(initial);
	});
	const [feedSourceType, setFeedSourceType] = useState(FEED_SOURCE_ALL);
	const [feedLoading, setFeedLoading] = useState(false);
	const [feedItems, setFeedItems] = useState([]);
	const [feedError, setFeedError] = useState(null);
	const [feedVisibleCount, setFeedVisibleCount] = useState(FEED_PAGE_SIZE);
	const [expandedFeedIds, setExpandedFeedIds] = useState(() => new Set());
	const [feedMinPriority, setFeedMinPriority] = useState(FEED_PRIORITY_MIN);
	const [feedHasFetched, setFeedHasFetched] = useState(false);
	const [showScrollTop, setShowScrollTop] = useState(false);
	const [feedSortField, setFeedSortField] = useState(FEED_SORT_FIELDS.PRIORITY);
	const [feedSortDirection, setFeedSortDirection] = useState(FEED_SORT_DIRECTIONS.DESC);
	const feedOutputRef = useRef(null);

	const activeTabConfig = useMemo(
		() => TABS.find((tab) => tab.id === activeTab),
		[activeTab],
	);
	const isNewsTab = activeTabConfig?.type === 'news';
	const isWebNewsTab = activeTab === WEB_NEWS_TAB_ID;
	const currentPeriodId = isNewsTab ? selectedPeriods[activeTab] ?? DAILY_PERIOD_ID : null;
	const currentPeriodConfig = isNewsTab
		? NEWS_PERIODS.find((period) => period.id === currentPeriodId) ?? NEWS_PERIODS[0]
		: null;

	const handlePeriodChange = useCallback(
		(event) => {
			const { value } = event.target;
			setSelectedPeriods((prev) => ({
				...prev,
				[activeTab]: value,
			}));
		},
		[activeTab],
	);

	const handleFetchNews = useCallback(async ({ targetDate } = {}) => {
		const isActiveNewsTab = activeTabConfig?.type === 'news';
		const targetPath = isActiveNewsTab ? currentPeriodConfig?.path : activeTabConfig?.path;

		if (!targetPath) {
			return;
		}

		const sourceType = activeTabConfig?.sourceType ?? null;
		const isNewsEndpoint = targetPath.startsWith('/news');
		const usesDailyDate =
			(isActiveNewsTab && currentPeriodId === DAILY_PERIOD_ID) || activeTabConfig?.id === WEB_NEWS_TAB_ID;

		setLoadingTab(activeTab);
		setTabErrors((prev) => ({
			...prev,
			[activeTab]: null,
		}));

		try {
			const params = new URLSearchParams();

			if (isNewsEndpoint && sourceType) {
				params.set('type', sourceType);
			}

			if (usesDailyDate) {
				const dateForQuery = targetDate ?? dailyDate;
				const formattedDate = formatDateForApi(dateForQuery);
				if (formattedDate) {
					params.set('targetDay', formattedDate);
				}
			}

			const paramString = params.toString();
			const url = `${SERVER_URL}${targetPath}${paramString ? `?${paramString}` : ''}`;

			const response = await fetch(
				url,
				{
					cache: 'no-store',
				},
			);

			if (!response.ok) {
				throw new Error(`Request failed with status ${response.status}`);
			}

			const payload = await response.json();

			if (payload?.errors?.length) {
				throw new Error(payload.errors.join(', '));
			}

			if (typeof payload?.response !== 'string') {
				throw new Error('Unexpected response format received from server.');
			}

			setTabResults((prev) => ({
				...prev,
				[activeTab]: payload.response,
			}));
		} catch (error) {
			setTabErrors((prev) => ({
				...prev,
				[activeTab]: error instanceof Error ? error.message : 'Unknown error encountered.',
			}));
		} finally {
			setLoadingTab(null);
		}
	}, [activeTab, activeTabConfig, currentPeriodConfig, currentPeriodId, dailyDate]);

	const fetchTrends = useCallback(async () => {
		setTrendsLoading(true);
		setTrendsError(null);

		try {
			const response = await fetch(`${SERVER_URL}/news/trends`, {
				headers: {
					accept: '*/*',
				},
				cache: 'no-store',
			});

			if (!response.ok) {
				throw new Error(`Trends request failed with status ${response.status}`);
			}

			const payload = await response.json();
			const trendList = payload?.response?.trends;

			if (!Array.isArray(trendList)) {
				throw new Error('Unexpected trends format received from server.');
			}

			const normalizedTrends = trendList
				.map((trend) => (typeof trend === 'string' ? trend.trim() : ''))
				.filter(Boolean);

			setTrends(normalizedTrends);
		} catch (error) {
			setTrends([]);
			setTrendsError(error instanceof Error ? error.message : 'Unknown error encountered.');
		} finally {
			setTrendsLoading(false);
		}
	}, []);

	const handleQuerySubmit = useCallback(async () => {
		const trimmedQuery = queryValue.trim();

		if (!trimmedQuery) {
			setQueryError('Provide a keyword or phrase to search for news.');
			return;
		}

		setQueryLoading(true);
		setQueryError(null);

		try {
			const response = await fetch(
				`${SERVER_URL}/news/topic?query=${encodeURIComponent(trimmedQuery)}`,
				{
					headers: {
						accept: '*/*',
					},
					cache: 'no-store',
				},
			);

			if (!response.ok) {
				throw new Error(`Query failed with status ${response.status}`);
			}

			const payload = await response.json();

			if (payload?.errors?.length) {
				throw new Error(payload.errors.join(', '));
			}

			const newsList = payload?.response?.newsLinesList;

			if (!Array.isArray(newsList)) {
				throw new Error('Unexpected response format received from server.');
			}

			const normalizedList = newsList.map((entry, index) => {
				return {
					id: entry?.id ?? `${entry?.timestamp ?? index}-${index}`,
					date: entry?.date ?? '',
					newsLine: entry?.newsLine ?? '',
					timestamp: entry?.timestamp ?? null,
					fullText: entry?.fullText ?? '',
					priority: getPriorityValue(entry),
				};
			});

			setQueryResult(normalizedList);
			setExpandedQueryIds(new Set());
			setQueryHasFetched(true);
		} catch (error) {
			setQueryResult(null);
			setQueryError(error instanceof Error ? error.message : 'Unknown error encountered.');
			setExpandedQueryIds(new Set());
			setQueryHasFetched(false);
		} finally {
			setQueryLoading(false);
		}
	}, [queryValue]);

	useEffect(() => {
		fetchTrends();
	}, [fetchTrends]);

	const activeResult = tabResults[activeTab];
	const isFeedTab = activeTab === FEED_TAB_ID;
	const activeError = isFeedTab ? feedError : tabErrors[activeTab];
	const isLoading = isFeedTab ? feedLoading : loadingTab === activeTab;
	const shouldShowPeriodSelection = isNewsTab;
	const isQueryTab = activeTab === QUERY_TAB_ID;
	const isPolymarketTab = activeTab === POLYMARKET_TAB_ID;
	const isDailyPeriod = (isNewsTab && currentPeriodId === DAILY_PERIOD_ID) || isWebNewsTab;
	const hasTrends = Array.isArray(trends) && trends.length > 0;
	const todayDate = useMemo(() => normalizeDate(new Date()), []);
	const dailyDateLabel = formatDateForApi(dailyDate);
	const feedStartInputValue = formatDateForApi(feedStartDate);
	const feedEndInputValue = formatDateForApi(feedEndDate);
	const isRightArrowDisabled =
		!isDailyPeriod || !(dailyDate instanceof Date) || dailyDate >= todayDate;
	const queryItems = Array.isArray(queryResult) ? queryResult : [];
	const filteredQueryItems = useMemo(() => {
		return queryItems.filter((item) => {
			const priority = getPriorityValue(item);
			return typeof priority === 'number' && priority >= queryMinPriority;
		});
	}, [queryItems, queryMinPriority]);
	const sortedQueryItems = useMemo(() => {
		const directionMultiplier =
			querySortDirection === FEED_SORT_DIRECTIONS.ASC ? 1 : -1;

		const getValueToCompare = (item) => {
			if (querySortField === FEED_SORT_FIELDS.PRIORITY) {
				const priority = getPriorityValue(item);
				return typeof priority === 'number' ? priority : null;
			}
			const dateValue = getFeedItemDateValue(item);
			return typeof dateValue === 'number' ? dateValue : null;
		};

		const getValueWithFallback = (value) => {
			if (typeof value === 'number' && !Number.isNaN(value)) {
				return value;
			}
			return directionMultiplier === 1
				? Number.POSITIVE_INFINITY
				: Number.NEGATIVE_INFINITY;
		};

		return [...filteredQueryItems].sort((a, b) => {
			const aValue = getValueWithFallback(getValueToCompare(a));
			const bValue = getValueWithFallback(getValueToCompare(b));

			if (aValue === bValue) {
				const aDate = getFeedItemDateValue(a) ?? 0;
				const bDate = getFeedItemDateValue(b) ?? 0;
				return directionMultiplier * (aDate - bDate);
			}

			return directionMultiplier * (aValue - bValue);
		});
	}, [filteredQueryItems, querySortDirection, querySortField]);
	const filteredQueryCount = sortedQueryItems.length;
	const hasQueryResults = filteredQueryCount > 0;
	const hasRawQueryResults = queryItems.length > 0;
	const shouldShowQuerySummary = queryHasFetched && !queryLoading && !queryError;
	const filteredFeedItems = useMemo(() => {
		return feedItems.filter((item) => {
			const priority = getPriorityValue(item);
			return typeof priority === 'number' && priority >= feedMinPriority;
		});
	}, [feedItems, feedMinPriority]);
	const sortedFeedItems = useMemo(() => {
		const directionMultiplier =
			feedSortDirection === FEED_SORT_DIRECTIONS.ASC ? 1 : -1;

		const getValueToCompare = (item) => {
			if (feedSortField === FEED_SORT_FIELDS.PRIORITY) {
				const priority = getPriorityValue(item);
				return typeof priority === 'number' ? priority : null;
			}
			const dateValue = getFeedItemDateValue(item);
			return typeof dateValue === 'number' ? dateValue : null;
		};

		const getValueWithFallback = (value) => {
			if (typeof value === 'number' && !Number.isNaN(value)) {
				return value;
			}
			return directionMultiplier === 1
				? Number.POSITIVE_INFINITY
				: Number.NEGATIVE_INFINITY;
		};

		return [...filteredFeedItems].sort((a, b) => {
			const aValue = getValueWithFallback(getValueToCompare(a));
			const bValue = getValueWithFallback(getValueToCompare(b));

			if (aValue === bValue) {
				const aDate = getFeedItemDateValue(a) ?? 0;
				const bDate = getFeedItemDateValue(b) ?? 0;
				return directionMultiplier * (aDate - bDate);
			}

			return directionMultiplier * (aValue - bValue);
		});
	}, [filteredFeedItems, feedSortDirection, feedSortField]);
	const visibleFeedItems = sortedFeedItems.slice(0, feedVisibleCount);
	const filteredFeedCount = sortedFeedItems.length;
	const hasFeedResults = filteredFeedCount > 0;
	const shouldShowFeedSummary = feedHasFetched && !feedLoading && !feedError;
	const canLoadMoreFeed = hasFeedResults && feedVisibleCount < sortedFeedItems.length;

	const getFormattedDate = (item) => {
		if (item.date) {
			const parsedDate = parseUtcDateString(item.date);
			if (parsedDate) {
				return parsedDate.toLocaleString(undefined, {
					year: 'numeric',
					month: 'short',
					day: '2-digit',
					hour: '2-digit',
					minute: '2-digit',
				});
			}
		}

		if (item.timestamp) {
			const d = new Date(item.timestamp * 1000);
			if (!Number.isNaN(d.getTime())) {
				return d.toLocaleString(undefined, {
					year: 'numeric',
					month: 'short',
					day: '2-digit',
					hour: '2-digit',
					minute: '2-digit',
				});
			}
		}

		return 'Date unavailable';
	};

	const getPriorityCircleCount = (priority) => {
		if (typeof priority !== 'number') {
			return 1;
		}

		if (priority < 82) {
			return 1;
		}

		if (priority < 87) {
			return 2;
		}

		return 3;
	};

	const renderPriorityCircles = (priority) => {
		const count = getPriorityCircleCount(priority);

		return Array.from({ length: count }).map((_, index) => (
			<span key={index} className="news-priority-circle" aria-hidden />
		));
	};

	const handleDailyShift = useCallback(
		(direction) => {
			const nextDate = normalizeDate(dailyDate);
			nextDate.setDate(nextDate.getDate() + direction);
			setDailyDate(nextDate);
			handleFetchNews({ targetDate: nextDate });
		},
		[dailyDate, handleFetchNews],
	);

	const handleFeedDateChange = useCallback((type, value) => {
		const normalizedValue = value ? normalizeDate(new Date(value)) : null;

		if (type === 'start') {
			setFeedStartDate(normalizedValue);
		} else {
			setFeedEndDate(normalizedValue);
		}
	}, []);

	const handleFeedFetch = useCallback(async ({ sourceType } = {}) => {
		const formattedStart = formatDateForApi(feedStartDate);
		const formattedEnd = formatDateForApi(feedEndDate);

		if (!formattedStart || !formattedEnd) {
			setFeedError('Select both start and end dates to load the feed.');
			return;
		}

		if (feedStartDate > feedEndDate) {
			setFeedError('Start date must be before or equal to the end date.');
			return;
		}

		setFeedLoading(true);
		setFeedError(null);

		try {
			const resolvedSourceType = sourceType ?? feedSourceType;
			const params = new URLSearchParams({
				start: formattedStart,
				end: formattedEnd,
			});

			if (resolvedSourceType && resolvedSourceType !== FEED_SOURCE_ALL) {
				params.set('type', resolvedSourceType);
			}

			const response = await fetch(
				`${SERVER_URL}/news/feed?${params.toString()}`,
				{
					headers: {
						accept: '*/*',
					},
					cache: 'no-store',
				},
			);

			if (!response.ok) {
				throw new Error(`Feed request failed with status ${response.status}`);
			}

			const payload = await response.json();

			if (payload?.errors?.length) {
				throw new Error(payload.errors.join(', '));
			}

			const newsLines = payload?.response?.newsLinesList;

			if (!Array.isArray(newsLines)) {
				throw new Error('Unexpected feed format received from server.');
			}

			const normalized = newsLines.map((entry, index) => ({
				id: entry?.id ?? `${entry?.timestamp ?? index}-${index}`,
				date: entry?.date ?? '',
				timestamp: entry?.timestamp ?? null,
				newsLine: entry?.newsLine ?? '',
				fullText: entry?.fullText ?? '',
				priority: getPriorityValue(entry),
			}));

			setFeedItems(normalized);
			setFeedVisibleCount(FEED_PAGE_SIZE);
			setExpandedFeedIds(new Set());
			setFeedHasFetched(true);
		} catch (error) {
			setFeedItems([]);
			setFeedVisibleCount(FEED_PAGE_SIZE);
			setExpandedFeedIds(new Set());
			setFeedHasFetched(false);
			setFeedError(error instanceof Error ? error.message : 'Unknown error encountered.');
		} finally {
			setFeedLoading(false);
		}
	}, [feedEndDate, feedSourceType, feedStartDate]);

	const handleFeedLoadMore = useCallback(() => {
		setFeedVisibleCount((prev) => Math.min(prev + FEED_PAGE_SIZE, sortedFeedItems.length));
	}, [sortedFeedItems.length]);

	const handleFeedShift = useCallback((direction) => {
		setFeedStartDate((prev) => {
			const base = prev instanceof Date ? new Date(prev) : new Date();
			base.setDate(base.getDate() + direction);
			return normalizeDate(base);
		});
		setFeedEndDate((prev) => {
			const base = prev instanceof Date ? new Date(prev) : new Date();
			base.setDate(base.getDate() + direction);
			return normalizeDate(base);
		});
	}, []);

	const toggleQueryItemExpansion = useCallback((itemId) => {
		setExpandedQueryIds((prev) => {
			const next = new Set(prev);
			if (next.has(itemId)) {
				next.delete(itemId);
			} else {
				next.add(itemId);
			}
			return next;
		});
	}, []);

	const isQueryItemExpanded = useCallback(
		(itemId) => expandedQueryIds.has(itemId),
		[expandedQueryIds],
	);

	const toggleFeedItemExpansion = useCallback((itemId) => {
		setExpandedFeedIds((prev) => {
			const next = new Set(prev);
			if (next.has(itemId)) {
				next.delete(itemId);
			} else {
				next.add(itemId);
			}
			return next;
		});
	}, []);

	const isFeedItemExpanded = useCallback(
		(itemId) => expandedFeedIds.has(itemId),
		[expandedFeedIds],
	);

	useEffect(() => {
		const container = feedOutputRef.current;
		const shouldTrackScroll = isFeedTab || isQueryTab;

		if (!shouldTrackScroll) {
			setShowScrollTop(false);
			return;
		}

		const handleScroll = () => {
			const containerScrolled = container ? container.scrollTop > 160 : false;
			const windowScrolled = window.scrollY > 160;
			setShowScrollTop(containerScrolled || windowScrolled);
		};

		handleScroll();

		if (container) {
			container.addEventListener('scroll', handleScroll, { passive: true });
		}
		window.addEventListener('scroll', handleScroll, { passive: true });

		return () => {
			if (container) {
				container.removeEventListener('scroll', handleScroll);
			}
			window.removeEventListener('scroll', handleScroll);
		};
	}, [isFeedTab, isQueryTab]);

	const handleTrendApply = useCallback((trendValue) => {
		setQueryValue(trendValue);
	}, []);

	const handleScrollToTop = useCallback(() => {
		const container = feedOutputRef.current;

		if (container) {
			container.scrollTo({ top: 0, behavior: 'smooth' });
		}

		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	return (
		<div className="news-panel">
			<header className="news-header">
				<h1 className="news-title">AI News</h1>
			</header>

			<div className="news-tabs">
				{TABS.map((tab) => (
					<button
						key={tab.id}
						type="button"
						className={`news-tab ${tab.id === activeTab ? 'news-tab--active' : ''}`}
						onClick={() => setActiveTab(tab.id)}
						disabled={loadingTab !== null && loadingTab !== tab.id}
					>
						{tab.label}
					</button>
				))}
			</div>

			<section className="news-content">
				<div
					className={`news-controls ${
						isFeedTab ? 'news-controls--feed' : isDailyPeriod ? 'news-controls--daily' : ''
					}`}
				>
					{isQueryTab ? (
						<>
							<div className="news-trends">
								<div className="news-trends__header">
									<span className="news-label">Trending prompts</span>
									<div className="news-trends__actions">
										{trendsLoading && <span className="news-trends__status">Refreshing...</span>}
										{!trendsLoading && trendsError && (
											<button type="button" className="news-trends__retry" onClick={fetchTrends}>
												Retry
											</button>
										)}
									</div>
								</div>
								{trendsError && !trendsLoading ? (
									<div className="news-trends__message">Unable to load trends: {trendsError}</div>
								) : hasTrends ? (
									<div className="news-trends__list" role="list">
										{trends.map((trend) => (
											<button
												key={trend}
												type="button"
												className="news-trend"
												onClick={() => handleTrendApply(trend)}
												disabled={queryLoading}
												role="listitem"
											>
												{trend}
											</button>
										))}
									</div>
								) : (
									<div className="news-trends__message">
										{trendsLoading ? 'Fetching latest prompts...' : 'No trending prompts available.'}
									</div>
								)}
							</div>
							<label className="news-label" htmlFor="news-query-input">
								Query
							</label>
							<input
								id="news-query-input"
								type="text"
								className="news-input"
								placeholder="Enter topic, ticker, or phrase"
								value={queryValue}
								onChange={(event) => setQueryValue(event.target.value)}
								disabled={queryLoading}
							/>
							<button
								type="button"
								className="news-button"
								onClick={handleQuerySubmit}
								disabled={queryLoading}
							>
								{queryLoading && <span className="news-spinner" aria-hidden />}
								<span>{queryLoading ? 'Searching...' : 'Submit Query'}</span>
							</button>
							<div className="news-query-filters">
								<div className="news-range-picker">
									<label className="news-label" htmlFor="query-min-priority">
										Min priority: <span className="news-priority-value">{queryMinPriority}</span>
									</label>
									<input
										id="query-min-priority"
										type="range"
										className="news-range-input"
										min={FEED_PRIORITY_MIN}
										max={FEED_PRIORITY_MAX}
										value={queryMinPriority}
										onChange={(event) => setQueryMinPriority(Number(event.target.value))}
										disabled={queryLoading}
									/>
								</div>
								<div className="news-sort-control">
									<span className="news-label">Sort</span>
									<div className="news-sort-options">
										{FEED_SORT_OPTIONS.map((option) => (
											<div key={option.id} className="news-sort-option">
												<span className="news-sort-option__label">{option.label}</span>
												<div
													className="news-sort-option__buttons"
													role="group"
													aria-label={`${option.label} sorting`}
												>
													{FEED_SORT_DIRECTION_OPTIONS.map((directionOption) => {
														const isActive =
															querySortField === option.id &&
															querySortDirection === directionOption.id;
														return (
															<button
																type="button"
																key={directionOption.id}
																className={`news-sort-button ${isActive ? 'news-sort-button--active' : ''}`}
																onClick={() => {
																	setQuerySortField(option.id);
																	setQuerySortDirection(directionOption.id);
																}}
																disabled={queryLoading}
																aria-pressed={isActive}
																aria-label={`${option.label} ${directionOption.label}`}
															>
																<svg
																	className={`news-sort-button__icon news-sort-button__icon--${directionOption.id}`}
																	viewBox="0 0 24 24"
																	role="img"
																	aria-hidden="true"
																>
																	<path d="M12 5l-7 7h4v7h6v-7h4z" fill="currentColor" />
																</svg>
															</button>
														);
													})}
												</div>
											</div>
										))}
									</div>
								</div>
							</div>
							{shouldShowQuerySummary && (
								<div className="news-feed-summary" role="status" aria-live="polite">
									<span className="news-feed-summary__label">News Found</span>
									<span className="news-feed-summary__value">{filteredQueryCount}</span>
								</div>
							)}
						</>
					) : isFeedTab ? (
						<div className="news-feed-controls">
							<div className="news-range-picker">
								<label className="news-label" htmlFor="feed-source-type">
									Source Type
								</label>
								<select
									id="feed-source-type"
									className="news-select"
									value={feedSourceType}
									onChange={(event) => {
										const { value } = event.target;
										setFeedSourceType(value);
										handleFeedFetch({ sourceType: value });
									}}
									disabled={feedLoading}
								>
									{FEED_SOURCE_TYPES.map((option) => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</div>
							<div className="news-range-picker">
								<label className="news-label" htmlFor="feed-start-date">
									Start date
								</label>
								<input
									id="feed-start-date"
									type="date"
									className="news-date-input"
									value={feedStartInputValue}
									onChange={(event) => handleFeedDateChange('start', event.target.value)}
									max={feedEndInputValue || undefined}
									disabled={feedLoading}
								/>
							</div>
							<div className="news-range-picker">
								<label className="news-label" htmlFor="feed-end-date">
									End date
								</label>
								<input
									id="feed-end-date"
									type="date"
									className="news-date-input"
									value={feedEndInputValue}
									onChange={(event) => handleFeedDateChange('end', event.target.value)}
									min={feedStartInputValue || undefined}
									disabled={feedLoading}
								/>
							</div>
							<div className="news-date-controls news-date-controls--feed">
								<button
									type="button"
									className="news-date-button"
									onClick={() => handleFeedShift(-1)}
									disabled={feedLoading}
									aria-label="Shift feed range back one day"
								>
									&lt;
								</button>
								<div className="news-date-display">- Day +</div>
								<button
									type="button"
									className="news-date-button"
									onClick={() => handleFeedShift(1)}
									disabled={feedLoading}
									aria-label="Shift feed range forward one day"
								>
									&gt;
								</button>
							</div>
							<div className="news-range-picker">
								<label className="news-label" htmlFor="feed-min-priority">
									Min priority: <span className="news-priority-value">{feedMinPriority}</span>
								</label>
								<input
									id="feed-min-priority"
									type="range"
									className="news-range-input"
									min={FEED_PRIORITY_MIN}
									max={FEED_PRIORITY_MAX}
									value={feedMinPriority}
									onChange={(event) => setFeedMinPriority(Number(event.target.value))}
									disabled={feedLoading}
								/>
							</div>
							<div className="news-sort-control">
								<span className="news-label">Sort</span>
								<div className="news-sort-options">
									{FEED_SORT_OPTIONS.map((option) => (
										<div key={option.id} className="news-sort-option">
											<span className="news-sort-option__label">{option.label}</span>
											<div className="news-sort-option__buttons" role="group" aria-label={`${option.label} sorting`}>
												{FEED_SORT_DIRECTION_OPTIONS.map((directionOption) => {
													const isActive =
														feedSortField === option.id &&
														feedSortDirection === directionOption.id;
													return (
														<button
															type="button"
															key={directionOption.id}
															className={`news-sort-button ${isActive ? 'news-sort-button--active' : ''}`}
															onClick={() => {
																setFeedSortField(option.id);
																setFeedSortDirection(directionOption.id);
															}}
															disabled={feedLoading}
															aria-pressed={isActive}
															aria-label={`${option.label} ${directionOption.label}`}
														>
															<svg
																className={`news-sort-button__icon news-sort-button__icon--${directionOption.id}`}
																viewBox="0 0 24 24"
																role="img"
																aria-hidden="true"
															>
																<path d="M12 5l-7 7h4v7h6v-7h4z" fill="currentColor" />
															</svg>
														</button>
													);
												})}
											</div>
										</div>
									))}
								</div>
							</div>
							<button
								type="button"
								className="news-button"
								onClick={handleFeedFetch}
								disabled={feedLoading}
							>
								{feedLoading && <span className="news-spinner" aria-hidden />}
								<span>{feedLoading ? 'Loading feed...' : 'Load Feed'}</span>
							</button>
							{shouldShowFeedSummary && (
								<div className="news-feed-summary" role="status" aria-live="polite">
									<span className="news-feed-summary__label">News Found</span>
									<span className="news-feed-summary__value">{filteredFeedCount}</span>
								</div>
							)}
						</div>
					) : (
						<>
							{isDailyPeriod && (
								<div className="news-date-controls">
									<button
										type="button"
										className="news-date-button"
										onClick={() => handleDailyShift(-1)}
										disabled={isLoading}
										aria-label="Previous day"
									>
										&lt;
									</button>
									<div className="news-date-display">{dailyDateLabel}</div>
									<button
										type="button"
										className="news-date-button"
										onClick={() => handleDailyShift(1)}
										disabled={isLoading || isRightArrowDisabled}
										aria-label="Next day"
									>
										&gt;
									</button>
								</div>
							)}
							{isPolymarketTab && (
								<p className="news-polymarket-heading">
									Polymarket: A Snapshot of Current Market Expectations for the Future
								</p>
							)}
							{shouldShowPeriodSelection && (
								<>
									<label className="news-label" htmlFor={`period-${activeTab}`}>
										Period
									</label>
									<select
										id={`period-${activeTab}`}
										className="news-select"
										value={currentPeriodId}
										onChange={handlePeriodChange}
										disabled={isLoading}
									>
										{NEWS_PERIODS.map((option) => (
											<option key={option.id} value={option.id}>
												{option.label}
											</option>
										))}
									</select>
								</>
							)}
							<button
								type="button"
								className="news-button"
								onClick={handleFetchNews}
								disabled={isLoading}
							>
								{isLoading && <span className="news-spinner" aria-hidden />}
								<span>{isLoading ? 'Fetching.' : 'Get News'}</span>
							</button>
						</>
					)}
				</div>
				<div className="news-output" ref={feedOutputRef}>
					{isQueryTab ? (
						<>
							{queryLoading && (
								<div className="news-placeholder">Looking up your query...</div>
							)}
							{!queryLoading && queryError && (
								<div className="news-error">Error: {queryError}</div>
							)}
							{!queryLoading && !queryError && hasQueryResults && (
								<div className="news-tiles">
									{sortedQueryItems.map((item, index) => {
										const itemId = item.id ?? `${index}`;
										const expanded = isQueryItemExpanded(itemId);
										return (
											<article
												key={itemId}
												className={`news-tile news-tile--expandable ${expanded ? 'news-tile--expanded' : ''}`}
												onClick={() => toggleQueryItemExpansion(itemId)}
												onKeyDown={(event) => {
													if (event.key === 'Enter' || event.key === ' ') {
														event.preventDefault();
														toggleQueryItemExpansion(itemId);
													}
												}}
												role="button"
												tabIndex={0}
												aria-expanded={expanded}
											>
												<header className="news-tile__header">
													<div className="news-tile__date">{getFormattedDate(item)}</div>
													<div className="news-tile__priority">
														<span className="news-tile__priority-label">Priority</span>
														<div
															className="news-tile__priority-circles"
															title={
																typeof item.priority === 'number'
																	? `Priority score: ${item.priority}`
																	: 'Priority score unavailable'
															}
															role="img"
															aria-label={
																typeof item.priority === 'number'
																	? `Priority score ${item.priority}`
																	: 'Priority score unavailable'
															}
														>
															{renderPriorityCircles(item.priority)}
														</div>
														<span className="news-tile__priority-value">
															{typeof item.priority === 'number' ? item.priority : '-'}
														</span>
													</div>
												</header>
												<p className="news-tile__text">{item.newsLine || 'No summary provided.'}</p>
												{expanded && (
													<div className="news-tile__full-text">
														<strong>Details:</strong>
														<p>{item.fullText || 'No additional context provided.'}</p>
													</div>
												)}
											</article>
										);
									})}
								</div>
							)}
							{!queryLoading && !queryError && !hasQueryResults && hasRawQueryResults && shouldShowQuerySummary && (
								<div className="news-placeholder">
									No news lines match the current priority threshold.
								</div>
							)}
							{!queryLoading && !queryError && !hasRawQueryResults && queryHasFetched && (
								<div className="news-placeholder">No news articles were returned for this query.</div>
							)}
							{!queryLoading && !queryError && !queryHasFetched && (
								<div className="news-placeholder">
									Enter a prompt to search for topic-specific news coverage.
								</div>
							)}
						</>
					) : isFeedTab ? (
						<>
							{feedLoading && (
								<div className="news-placeholder">Loading news feed for selected range.</div>
							)}
							{!feedLoading && feedError && <div className="news-error">Error: {feedError}</div>}
							{!feedLoading && !feedError && hasFeedResults && (
								<>
									<div className="news-tiles">
										{visibleFeedItems.map((item, index) => {
											const itemId = item.id ?? `${index}`;
											const expanded = isFeedItemExpanded(itemId);
											return (
												<article
													key={itemId}
													className={`news-tile news-tile--expandable ${expanded ? 'news-tile--expanded' : ''}`}
													onClick={() => toggleFeedItemExpansion(itemId)}
													onKeyDown={(event) => {
														if (event.key === 'Enter' || event.key === ' ') {
															event.preventDefault();
															toggleFeedItemExpansion(itemId);
														}
													}}
													role="button"
													tabIndex={0}
													aria-expanded={expanded}
												>
													<header className="news-tile__header">
														<div className="news-tile__date">{getFormattedDate(item)}</div>
														<div className="news-tile__priority">
															<span className="news-tile__priority-label">Priority</span>
															<div
																className="news-tile__priority-circles"
																title={
																	typeof item.priority === 'number'
																		? `Priority score: ${item.priority}`
																		: 'Priority score unavailable'
																}
																role="img"
																aria-label={
																	typeof item.priority === 'number'
																		? `Priority score ${item.priority}`
																		: 'Priority score unavailable'
																}
															>
																{renderPriorityCircles(item.priority)}
															</div>
															<span className="news-tile__priority-value">
																{typeof item.priority === 'number' ? item.priority : '-'}
															</span>
														</div>
													</header>
													<p className="news-tile__text">{item.newsLine || 'No summary provided.'}</p>
													{expanded && (
														<div className="news-tile__full-text">
															<strong>Details:</strong>
															<p>{item.fullText || 'No additional context provided.'}</p>
														</div>
													)}
												</article>
											);
										})}
									</div>
									{canLoadMoreFeed && (
										<button
											type="button"
											className="news-button news-load-more"
											onClick={handleFeedLoadMore}
										>
											Load More
										</button>
									)}
								</>
							)}
							{!feedLoading && !feedError && !hasFeedResults && shouldShowFeedSummary && (
								<div className="news-placeholder">
									No news lines match the current priority threshold.
								</div>
							)}
							{!feedLoading && !feedError && !shouldShowFeedSummary && (
								<div className="news-placeholder">
									Select a date range and load the feed to see aggregated news lines.
								</div>
							)}
						</>
					) : (
						<>
							{isLoading && (
								<div className="news-placeholder">Fetching the latest articles.</div>
							)}
							{!isLoading && activeError && (
								<div className="news-error">Error: {activeError}</div>
							)}
							{!isLoading && !activeError && activeResult && (
								<div className="news-markdown">
									<ReactMarkdown>{activeResult}</ReactMarkdown>
								</div>
							)}
							{!isLoading && !activeError && !activeResult && (
								<div className="news-placeholder">
									Choose a source and fetch news for the selected timeframe.
								</div>
							)}
						</>
					)}
				</div>
			</section>
			{(isFeedTab || isQueryTab) && (
				<button
					type="button"
					className={`news-scroll-top ${showScrollTop ? 'news-scroll-top--visible' : ''}`}
					onClick={handleScrollToTop}
					aria-label="Scroll to top"
				>
					<svg className="news-scroll-top__icon" viewBox="0 0 24 24" role="img" aria-hidden="true">
						<path d="M12 6l-6 6h4v6h4v-6h4z" fill="currentColor" />
					</svg>
				</button>
			)}
		</div>
	);
};
